import React, { useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { downloadFile, extractFileNameFromHeaders } from '../../../../util/DownloadUtil';
import { convertDateToUTC, DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';

import {
  retrieveDataCaptureTimelineReport,
  downloadDataCaptureTimelineReport,
  retrieveDataCaptureStaffReport,
  downloadDataCaptureStaffReport,
} from '../../../../services/ReportService';

import { Checkbox } from '../../../common/checkbox/Checkbox';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Table } from '../../../common/table/Table';

import './DataCapture.css';

export const DataCapture = ({ context, setSpinnerText, showToaster, studies, downloadEnabled }) => {
  const [reportType, setReportType] = useState('timeline');
  const [study, setStudy] = useState(undefined);
  const [dateFrom, setDateFrom] = useState(undefined);
  const [dateTo, setDateTo] = useState(undefined);
  const [results, setResults] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const generateTimelineReport = async () => {
    setSpinnerText('Generating Timeline Report');
    const [data, error] = await retrieveDataCaptureTimelineReport(
      study.value.studyExternalReference,
      study.value.locationExternalReference,
      context.username
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        setResults(
          data.dataCaptureTimelineVOS.map((vo) => ({
            participantID: vo.participantID,
            visitCode: vo.visitCode,
            visitDate: vo.visitDate,
            dataCaptureDate: vo.dataCaptureDate,
            dataCapturer: vo.dataCapturer,
            days: vo.days,
          }))
        );
        setShowTable(true);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const generateStaffReport = async () => {
    setSpinnerText('Generating Staff Report');
    const [data, error] = await retrieveDataCaptureStaffReport(
      convertDateToUTC(dateFrom),
      convertDateToUTC(dateTo),
      context.username
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        setResults(
          data.dataCapturePerStaffVOS.map((vo) => ({
            dataCapturer: vo.dataCapturer,
            numberOfStudies: vo.numberOfStudies,
            visitsCaptured: vo.visitsCaptured,
          }))
        );
        setShowTable(true);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const handleGenerateClick = async () => {
    if (reportType === 'timeline') {
      if (!study) {
        showToaster({
          responseStatus: 'Error',
          responseMessage: 'Study is required',
        });
        return;
      }

      await generateTimelineReport();
    } else {
      if (!dateFrom || !dateTo) {
        showToaster({
          responseStatus: 'Error',
          responseMessage: 'Date From and Date To are required',
        });
        return;
      }

      await generateStaffReport();
    }
  };

  const handleResetFiltersClick = () => {
    setStudy(undefined);
    setDateFrom(undefined);
    setDateTo(undefined);
    setResults([]);
    setShowTable(false);
  };

  const handleDownloadExcelClick = async () => {
    let data;
    let headers;
    let error;

    if (reportType === 'timeline') {
      setSpinnerText('Downloading Data Capture Timeline Report');
      [data, headers, error] = await downloadDataCaptureTimelineReport(
        study.value.studyExternalReference,
        study.value.locationExternalReference,
        context.username
      );
    } else {
      setSpinnerText('Downloading Data Capture Staff Report');
      [data, headers, error] = await downloadDataCaptureStaffReport(dateFrom, dateTo, context.username);
    }

    if (data) {
      const name = extractFileNameFromHeaders(headers);
      downloadFile(data, name);
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(undefined);
  };

  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const getTableName = () => {
    if (reportType === 'timeline') {
      return study.value.name;
    } else {
      return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    }
  };

  return (
    <section className="data-capture">
      {!showTable ? (
        <section className="data-capture-form">
          <h1 className="data-capture-heading">Generate Report</h1>

          <div className="data-capture-form-inputs">
            <div className="data-capture-checkbox-container">
              <label className="input-label">Type of Report:</label>
              <Checkbox
                checked={reportType === 'timeline'}
                onChange={() => setReportType('timeline')}
                color="#000000"
                label="Timeline"
                fillStyle
              />
              <Checkbox
                checked={reportType === 'staff'}
                onChange={() => setReportType('staff')}
                color="#000000"
                label="Staff"
                fillStyle
              />
            </div>

            {reportType === 'timeline' && (
              <div className="input-container">
                <label className="input-label">Study:</label>
                <Select
                  options={studies}
                  onChange={(option) => setStudy(option)}
                  value={study}
                  className="input-select"
                  classNamePrefix="input-select"
                  closeMenuOnSelect
                />
              </div>
            )}

            {reportType === 'staff' && (
              <div>
                <div className="input-container">
                  <label className="input-label">Date From:</label>
                  <DatePicker
                    selected={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    className="text-input"
                    dateFormat="dd/MM/yyy"
                    showPopperArrow={false}
                    placeholderText="Select..."
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                </div>
                <div className="input-container">
                  <label className="input-label">Date To:</label>
                  <DatePicker
                    selected={dateTo}
                    onChange={(date) => setDateTo(date)}
                    className="text-input"
                    dateFormat="dd/MM/yyy"
                    showPopperArrow={false}
                    placeholderText="Select..."
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                </div>
              </div>
            )}
          </div>
          <SaveButton title="Generate" onClick={handleGenerateClick} color="#077187" />
        </section>
      ) : (
        <section className="data-capture-table-container">
          <div className="data-capture-table">
            <h1 className="data-capture-table-heading">
              {reportType === 'timeline' ? 'Data Capture Timeline Report' : 'Data Capture per Staff Report'}
            </h1>
            <Table
              tableData={results}
              tableTitle={getTableName()}
              maxRowCount={9}
              hiddenColumns={[]}
              filterEnabled
              searchEnabled
            />
          </div>
          <div className="data-capture-table-buttons">
            <SaveButton
              title="Reset Filters"
              onClick={handleResetFiltersClick}
              color="white"
              textColor="#077187"
              border="1px solid #077187"
            />
            {downloadEnabled && (
              <SaveButton
                title="Download Excel"
                onClick={handleDownloadExcelClick}
                color="#077187"
                border="1px solid #077187"
              />
            )}
          </div>
        </section>
      )}
    </section>
  );
};
